// Live chart of a chart card (ChartCardWidget's Open): its own Dockview window
// (a full-screen overlay on mobile). All bars are loaded once (POST
// /ibkr/chart), then every 0.5s the bars since the last one (POST
// /ibkr/chart/since) are merged in — plain requests, no websockets. The backend
// keeps the chart subscribed while it's polled and drops it 90s after the last
// request; the window then gets a new subId and reloads everything.
// Stock: candles + volume. Option: its underlying's candles + volume, the
// option's candles and the IV of each option bar (rflx-backend ibkr_fast.py get_chart),
// drawn with Lightweight Charts (LwCharts.tsx).

import { DockviewApi, IDockviewPanelProps } from "dockview-react";
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LwCandles, LwIv, nyTime } from "./LwCharts";
import { CHART_POLL_TIMEOUT, chartData, chartSince, errorText } from "./api";
import { useIbkrData } from "./poller";
import { Bar, ChartData, ChartParams, ChartRange, ContractInfo, IvPoint, QuoteRow } from "./types";
import { C, Chip, FONT, StatusDot, companyName, fmt, optionLine } from "./ui";
import { referencePrice } from "./windows";

const POLL_INTERVAL = 500;
const LOG_VOLUME_KEY = "ibkr_chart_log_volume";
// an option window's charts (it scrolls when they don't fit): share of the window height, minimum px
const UNDER_SHARE = 0.55, UNDER_MIN = 380;
const OPT_SHARE = 0.45, OPT_MIN = 320;
const IV_SHARE = 0.3, IV_MIN = 220;
// an empty strip left of an option window's charts: the charts take the mouse
// wheel (zoom), the strip scrolls the window
const SCROLL_GUTTER = 44;

export const chartHours = (outsideRth: boolean) => (outsideRth ? "outside RTH" : "regular hours");

// a chart without a range: the last 7 trading days (rflx-backend ibkr_fast.py DEFAULT_CHART_DAYS)
const DEFAULT_CHART_DAYS = 7;

// the range switches of a chart window's header
const QUICK_RANGES: { label: string; days: number }[] = [
  { label: "1D", days: 1 },
  { label: "7D", days: 7 },
];

const rangeTime = (t: number) => nyTime(t).slice(0, 16);

// "1 day", "7 days", "300 candles", "2026-10-01 09:30 – now" (New York)
export function chartRange(r: ChartRange): string {
  if (r.count != null) return `${r.count} candles`;
  if (r.start != null) return `${rangeTime(r.start)} – ${r.end != null ? rangeTime(r.end) : "now"}`;
  const days = r.days ?? DEFAULT_CHART_DAYS;
  return `${days} day${days > 1 ? "s" : ""}`;
}

const rangeOf = ({ days, start, end, count }: ChartRange): ChartRange => ({ days, start, end, count });

// ---- Dockview ------------------------------------------------------------------

export function openChartWindow(api: DockviewApi, params: ChartParams, title: string) {
  // the id's prefix is the component name (the tab's detach button looks up its size by it)
  api.addPanel({
    id: `ibkr_chart_${Date.now()}`,
    component: "ibkr_chart",
    title,
    floating: { width: 900, height: 700, x: 80, y: 60 },
    params,
  });
}

// polls only while its tab is shown
export function ChartWindow(props: IDockviewPanelProps<ChartParams>) {
  const [visible, setVisible] = useState(props.api.isVisible);
  useEffect(() => {
    const sub = props.api.onDidVisibilityChange((e) => setVisible(e.isVisible));
    return () => sub.dispose();
  }, [props.api]);
  return <ChartView params={props.params} active={visible} />;
}

// ---- data ------------------------------------------------------------------------

// the rows from `since` on are replaced by the fresh ones (the last bar comes
// again, it may have changed); null when nothing changed
function mergeSince<T extends { t: number }>(old: T[], fresh: T[] | undefined, since: number | null): T[] | null {
  if (!fresh || fresh.length === 0) return null;
  const cut = since ?? fresh[0].t;
  let i = old.length;
  while (i > 0 && old[i - 1].t >= cut) i--;
  if (JSON.stringify(old.slice(i)) === JSON.stringify(fresh)) return null;
  return [...old.slice(0, i), ...fresh];
}

// with a count, the bars are trimmed to it and an option's underlying and IV
// to the first option bar; a time range keeps growing with the new bars
function merge(data: ChartData, fresh: { bars: Bar[]; underlying?: Bar[]; iv?: IvPoint[] }, count: number | undefined, since: number | null, underSince: number | null): ChartData | null {
  const bars = mergeSince(data.bars, fresh.bars, since);
  const under = data.underlying ? mergeSince(data.underlying.bars, fresh.underlying, underSince) : null;
  const iv = data.iv ? mergeSince(data.iv, fresh.iv, since) : null;
  if (!bars && !under && !iv) return null;
  if (count == null) {
    const next: ChartData = { ...data, bars: bars ?? data.bars };
    if (data.underlying) next.underlying = { ...data.underlying, bars: under ?? data.underlying.bars };
    if (data.iv) next.iv = iv ?? data.iv;
    return next;
  }
  const next: ChartData = { ...data, bars: (bars ?? data.bars).slice(-count) };
  const first = next.bars[0]?.t;
  if (data.underlying) {
    const underBars = under ?? data.underlying.bars;
    next.underlying = { ...data.underlying, bars: first != null ? underBars.filter((b) => b.t >= first) : underBars.slice(-count) };
  }
  if (data.iv) next.iv = first != null ? (iv ?? data.iv).filter((p) => p.t >= first) : iv ?? data.iv;
  return next;
}

// loads the chart, then polls while `active`
function useChart(params: ChartParams, active: boolean) {
  const [data, setData] = useState<ChartData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastOk, setLastOk] = useState(0);
  // the backend is (re)subscribing the chart: polls answer "pending" until it has the bars
  const [pending, setPending] = useState(false);
  const [now, setNow] = useState(Date.now());
  const dataRef = useRef<ChartData | null>(null);
  const inFlight = useRef(false);
  const loadedAt = useRef(0);
  const { conId, barSize, outsideRth, days, start, end, count } = params;

  const load = useCallback(async () => {
    inFlight.current = true;
    try {
      const d = await chartData({ conId, barSize, outsideRth, days, start, end, count });
      dataRef.current = d;
      loadedAt.current = Math.floor(Date.now() / 1000);
      setData(d);
      setError(null);
      setPending(false);
      setLastOk(Date.now());
    } catch (e) {
      setError(errorText(e));
    } finally {
      inFlight.current = false;
    }
  }, [conId, barSize, outsideRth, days, start, end, count]);

  useEffect(() => {
    dataRef.current = null;
    setData(null);
    load();
  }, [load]);

  useEffect(() => {
    if (!active) return;
    const timer = setInterval(async () => {
      setNow(Date.now());
      const d = dataRef.current;
      if (inFlight.current) return;
      if (!d) {
        load(); // the first load failed: retry
        return;
      }
      inFlight.current = true;
      // without bars yet: the ones since the load
      const since = d.bars.length ? d.bars[d.bars.length - 1].t : loadedAt.current;
      const underBars = d.underlying?.bars ?? [];
      const underSince = d.underlying ? (underBars.length ? underBars[underBars.length - 1].t : loadedAt.current) : null;
      try {
        const fresh = await chartSince({ conId, barSize, outsideRth, days, start, end, count }, since, underSince);
        if (fresh.pending) {
          setPending(true);
          setError(null);
          return;
        }
        if (fresh.subId !== d.subId) {
          inFlight.current = false;
          await load(); // subscribed again (dropped after 90s without requests, backend restart)
          return;
        }
        const next = dataRef.current === d ? merge(d, fresh, count, since, underSince) : null;
        if (next) {
          dataRef.current = next;
          setData(next);
        }
        setError(null);
        setLastOk(Date.now());
      } catch (e) {
        setError(errorText(e));
      } finally {
        inFlight.current = false;
      }
    }, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [active, load, conId, barSize, outsideRth, days, start, end, count]);

  const freshness: "live" | "stale" | "loading" | "reloading" = !data ? (error ? "stale" : "loading")
    : pending && !error ? "reloading" : now - lastOk < CHART_POLL_TIMEOUT && !error ? "live" : "stale";
  return { data, error, freshness };
}

// ---- charts ----------------------------------------------------------------------

function useHeight(ref: React.RefObject<HTMLDivElement | null>) {
  const [height, setHeight] = useState(500);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setHeight(Math.max(240, Math.floor(entry.contentRect.height))));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return height;
}

const quotePrice = (row: QuoteRow | undefined) => (row && "quote" in row ? referencePrice(row.quote) : null);

function Charts({ data, params, height, quotes, logVolume }: { data: ChartData; params: ChartParams; height: number; quotes: QuoteRow[] | undefined; logVolume: boolean }) {
  const option = data.contract.secType === "OPT";
  const price = quotePrice(quotes?.[0]);
  const under = data.underlying;
  const caption: CSSProperties = { height: 20, lineHeight: "20px", color: C.muted, paddingLeft: 8 };
  if (!option) {
    return data.bars.length
      ? <LwCandles bars={data.bars} resetKey={data.subId} barSize={params.barSize} height={height} price={price} logVolume={logVolume} />
      : <Empty height={height} />;
  }
  const hUnder = Math.max(UNDER_MIN, Math.floor(height * UNDER_SHARE));
  const hOpt = Math.max(OPT_MIN, Math.floor(height * OPT_SHARE));
  const hIv = Math.max(IV_MIN, Math.floor(height * IV_SHARE));
  return (
    <div style={{ paddingLeft: SCROLL_GUTTER, background: C.headerBg }}>
      <div style={{ background: "#fff" }}>
      <div style={caption}>{under?.contract.label} ({chartHours(params.outsideRth)})</div>
      {under?.bars.length
        ? <LwCandles bars={under.bars} resetKey={data.subId} barSize={params.barSize} height={hUnder} price={quotePrice(quotes?.[1])} logVolume={logVolume} />
        : <Empty height={hUnder} />}
      <div style={caption}>{data.contract.label} (regular hours, bars with trades only)</div>
      {data.bars.length
        ? <LwCandles bars={data.bars} resetKey={data.subId} barSize={params.barSize} height={hOpt} price={price} logVolume={logVolume} />
        : <Empty height={hOpt} />}
      <div style={caption}>IV per option bar (r = 4%)</div>
      {data.iv?.length
        ? <LwIv points={data.iv} resetKey={data.subId} barSize={params.barSize} height={hIv} />
        : <Empty height={hIv} what="No IV (no option bars with trades)" />}
      </div>
    </div>
  );
}

function Empty({ height = 200, what = "No bars from IB for this range" }: { height?: number; what?: string }) {
  return <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>{what}</div>;
}

// ---- data tab --------------------------------------------------------------------

// explicit borders / margin: the app's global table styles add a grid and a top margin
const cell: CSSProperties = { padding: "2px 8px", textAlign: "right", whiteSpace: "nowrap", border: 0, borderBottom: `1px solid ${C.divider}` };
const headCell: CSSProperties = { ...cell, position: "sticky", top: 0, background: C.headerBg, fontWeight: 600, borderBottom: `1px solid ${C.border}` };

function BarTable({ title, bars, iv }: { title: string; bars: Bar[]; iv?: IvPoint[] }) {
  const ivByTime = useMemo(() => new Map((iv ?? []).map((p) => [p.t, p])), [iv]);
  const rows = useMemo(() => [...bars].reverse(), [bars]);
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <div style={{ fontWeight: 600, padding: "6px 0" }}>{title} — {bars.length} bars, newest first</div>
      <div style={{ overflow: "auto", flex: 1, border: `1px solid ${C.border}` }}>
        <table style={{ borderCollapse: "collapse", width: "100%", margin: 0, fontSize: 11.5 }}>
          <thead>
            <tr>
              {["Time (New York)", "Open", "High", "Low", "Close", "Volume", "Avg", ...(iv ? ["Underlying avg", "IV"] : [])].map((h) => (
                <th key={h} style={headCell}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => {
              const p = ivByTime.get(b.t);
              return (
                <tr key={b.t}>
                  <td style={{ ...cell, textAlign: "left" }}>{nyTime(b.t)}</td>
                  <td style={cell}>{fmt(b.o)}</td>
                  <td style={cell}>{fmt(b.h)}</td>
                  <td style={cell}>{fmt(b.l)}</td>
                  <td style={cell}>{fmt(b.c)}</td>
                  <td style={cell}>{b.v ?? "—"}</td>
                  <td style={cell}>{fmt(b.avg, 3)}</td>
                  {iv && <td style={cell}>{p ? fmt(p.underlyingAvg, 3) : "—"}</td>}
                  {iv && <td style={cell}>{p ? `${(p.iv * 100).toFixed(1)}%` : "—"}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- window ----------------------------------------------------------------------

export function ChartView({ params: initial, active, onClose }: { params: ChartParams; active: boolean; onClose?: () => void }) {
  // the header's 1D / 7D switch the range in place
  const [range, setRange] = useState<ChartRange>(() => rangeOf(initial));
  const params = useMemo<ChartParams>(() => ({ conId: initial.conId, barSize: initial.barSize, outsideRth: initial.outsideRth, ...range }),
    [initial.conId, initial.barSize, initial.outsideRth, range]);
  const { data, error, freshness } = useChart(params, active);
  const [tab, setTab] = useState<"chart" | "data">("chart");
  // remembered per browser (a viewer convenience; storage may be unavailable)
  const [logVolume, setLogVolume] = useState(() => {
    try {
      return localStorage.getItem(LOG_VOLUME_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleLogVolume = () => {
    const next = !logVolume;
    setLogVolume(next);
    try {
      localStorage.setItem(LOG_VOLUME_KEY, next ? "1" : "0");
    } catch {}
  };
  const bodyRef = useRef<HTMLDivElement>(null);
  const height = useHeight(bodyRef);
  const contract: ContractInfo | undefined = data?.contract;
  const underConId = data?.underlying?.contract.conId;
  const quotes = useIbkrData<QuoteRow[]>("quotes", { conIds: underConId ? [params.conId, underConId] : [params.conId] }, active && !!data);

  const status = freshness === "loading" || freshness === "reloading"
    ? <StatusDot color={C.disabled} label={freshness} />
    : <StatusDot color={freshness === "live" ? C.live : C.stale} label={freshness} />;
  const tabButton = (t: "chart" | "data", label: string) => (
    <button onClick={() => setTab(t)} style={{ height: 26, padding: "0 12px", border: `1px solid ${C.border}`, background: tab === t ? C.text : "#fff", color: tab === t ? "#fff" : C.text, fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>
      {label}
    </button>
  );
  const rangeButton = ({ label, days }: { label: string; days: number }) => {
    const on = params.days === days || (days === DEFAULT_CHART_DAYS && params.days == null && params.start == null && params.count == null);
    return (
      <button key={label} onClick={() => setRange({ days })} aria-pressed={on}
        style={{ height: 26, padding: "0 10px", border: `1px solid ${C.border}`, background: on ? C.text : "#fff", color: on ? "#fff" : C.text, fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>
        {label}
      </button>
    );
  };

  return (
    <div style={{ height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", background: "#fff", color: C.text, fontFamily: FONT, fontSize: 12, lineHeight: 1.5 }}>
      <div style={{ flex: "none", display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 10px", background: C.headerBg, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
          <span><Chip>{contract?.symbol ?? "…"}</Chip> {companyName(contract)}</span>
          <span style={{ color: C.muted }}>
            {contract && optionLine(contract) ? `${optionLine(contract)} · ` : ""}{params.barSize} · {chartRange(params)} · {chartHours(params.outsideRth)}
          </span>
        </div>
        <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex" }}>{QUICK_RANGES.map(rangeButton)}</div>
          {tabButton("chart", "Chart")}
          {tabButton("data", "Data")}
          {tab === "chart" && (
            <button onClick={toggleLogVolume} title="Volume scale" aria-pressed={logVolume}
              style={{ height: 26, padding: "0 12px", border: `1px solid ${C.border}`, background: logVolume ? C.text : "#fff", color: logVolume ? "#fff" : C.text, fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>
              Vol: {logVolume ? "log" : "linear"}
            </button>
          )}
          {status}
          {onClose && (
            <button onClick={onClose} style={{ height: 26, padding: "0 12px", border: `1px solid ${C.border}`, background: "#fff", fontFamily: "inherit", fontSize: 12 }}>Close</button>
          )}
        </div>
      </div>
      {error && <div style={{ flex: "none", padding: "4px 10px", color: C.cancelled, overflowWrap: "anywhere" }}>{error}</div>}
      <div ref={bodyRef} style={{ flex: 1, minHeight: 0, overflow: tab === "chart" && contract?.secType !== "OPT" ? "hidden" : "auto", padding: tab === "data" ? "0 10px 10px" : 0, display: "flex", flexDirection: "column" }}>
        {!data ? (
          <Empty height={height} what={error ? "Chart not loaded, retrying…" : "Loading bars from IB…"} />
        ) : tab === "chart" ? (
          <Charts data={data} params={params} height={height} quotes={quotes.data} logVolume={logVolume} />
        ) : (
          <>
            <div style={{ padding: "8px 0 0", color: C.muted }}>
              Request: conId {params.conId}, bar size {params.barSize}, {chartRange(params)}, {chartHours(params.outsideRth)}
              {data.underlying ? " (the option's bars: regular hours)" : ""}, TRADES.
              {data.underlying ? " IV: Black-Scholes from the option bar's average and the nearest underlying bar's average, r = 4%, expiry 16:00 New York." : ""}
            </div>
            <BarTable title={data.contract.label} bars={data.bars} iv={data.iv} />
            {data.underlying && <BarTable title={data.underlying.contract.label} bars={data.underlying.bars} />}
          </>
        )}
      </div>
    </div>
  );
}
